import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Icon from '../components/AppIcon';
import { cn } from '../utils/cn';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, title?: string, duration?: number) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', title?: string, duration: number = 5000) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: Toast = { id, type, message, title, duration };
      
      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          hideToast(id);
        }, duration);
      }
    },
    []
  );

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const getToastStyles = (type: ToastType) => {
    const styles = {
      success: 'bg-green-50 border-green-500 text-green-800',
      error: 'bg-red-50 border-red-500 text-red-800',
      warning: 'bg-yellow-50 border-yellow-500 text-yellow-800',
      info: 'bg-blue-50 border-blue-500 text-blue-800',
    };
    return styles[type];
  };

  const getToastIcon = (type: ToastType) => {
    const icons = {
      success: 'CheckCircle2',
      error: 'XCircle',
      warning: 'AlertTriangle',
      info: 'Info',
    };
    return icons[type];
  };

  const getIconColor = (type: ToastType) => {
    const colors = {
      success: '#16a34a',
      error: '#dc2626',
      warning: '#ca8a04',
      info: '#2563eb',
    };
    return colors[type];
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[1000] space-y-3 max-w-md">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg animate-in slide-in-from-right duration-300',
              getToastStyles(toast.type)
            )}
            role="alert"
          >
            <Icon 
              name={getToastIcon(toast.type)} 
              size={24} 
              color={getIconColor(toast.type)}
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1 min-w-0">
              {toast.title && (
                <h4 className="font-semibold mb-1">{toast.title}</h4>
              )}
              <p className="text-sm">{toast.message}</p>
            </div>
            <button
              onClick={() => hideToast(toast.id)}
              className="flex-shrink-0 ml-2 hover:opacity-70 transition-opacity"
              aria-label="Close notification"
            >
              <Icon name="X" size={18} color="currentColor" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
